/**
 * Public Passport QR Page
 * One URL. Same page for everyone. No login. No role detection.
 * Content changes with passport STATUS (dormant/active/breach/closed).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Shield, Thermometer, Lock, CheckCircle, AlertTriangle,
  Loader2, Download, MapPin, Clock, Truck, Package, Hash, Wifi
} from 'lucide-react';
import jsPDF from 'jspdf';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function PassportQRPublicPage() {
  const { passportUuid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/v1/public/passport/${passportUuid}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Passport not found');
    } finally {
      setLoading(false);
    }
  }, [passportUuid]);

  useEffect(() => {
    if (passportUuid) fetchData();
  }, [passportUuid, fetchData]);

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  // Generate co-branded compliance PDF
  const generateCertificate = async () => {
    if (!data) return;
    setGenerating(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      let y = 15;

      // Header — Co-branding
      pdf.setFillColor(13, 17, 23);
      pdf.rect(0, 0, w, 45, 'F');

      // Track0tic brand (left)
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Track0tic\u2122', 15, 20);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(data.tagline || 'track verified', 15, 27);
      pdf.text('by Track0tic', 15, 32);

      // Client brand (right)
      if (data.client_display_name) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(data.client_display_name, w - 15, 20, { align: 'right' });
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Verified Cold Chain Partner', w - 15, 27, { align: 'right' });
      }
      if (data.distributor_name && data.distributor_name !== data.client_display_name) {
        pdf.setFontSize(8);
        pdf.text(`Distributor: ${data.distributor_name}`, w - 15, 32, { align: 'right' });
      }

      // Teal accent line
      pdf.setFillColor(13, 148, 136);
      pdf.rect(0, 45, w, 2, 'F');
      y = 55;

      // Verification badge
      const allPass = !data.has_breach;
      if (allPass) {
        pdf.setFillColor(16, 185, 129);
        pdf.roundedRect(15, y, w - 30, 12, 3, 3, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('VERIFIED — Full track verified', w / 2, y + 8, { align: 'center' });
      } else {
        pdf.setFillColor(239, 68, 68);
        pdf.roundedRect(15, y, w - 30, 12, 3, 3, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('EXCURSION DETECTED — Temperature Breach Recorded', w / 2, y + 8, { align: 'center' });
      }
      y += 20;

      // Passport Info
      pdf.setTextColor(60, 60, 60);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const info = [
        ['Passport UUID', data.passport_uuid],
        ['Invoice', data.invoice_number || '—'],
        ['Product', data.product_description || '—'],
        ['Customer', data.customer_name || '—'],
        ['Chain', data.custodian_chain?.join(' → ') || '—'],
        ['Total Entries', String(data.total_entries)],
        ['Status', data.status?.toUpperCase()],
      ];
      info.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${label}:`, 15, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(value, 55, y);
        y += 6;
      });
      y += 5;

      // Entries table
      pdf.setFillColor(240, 240, 240);
      pdf.rect(15, y, w - 30, 8, 'F');
      pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      const cols = [15, 25, 55, 85, 110, 135, 160];
      pdf.text('#', cols[0], y + 5);
      pdf.text('Type', cols[1], y + 5);
      pdf.text('Custodian', cols[2], y + 5);
      pdf.text('Temp Range', cols[3], y + 5);
      pdf.text('Result', cols[4], y + 5);
      pdf.text('Duration', cols[5], y + 5);
      pdf.text('SHA-256', cols[6], y + 5);
      y += 10;

      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(40, 40, 40);
      data.entries?.forEach((entry) => {
        if (y > 270) { pdf.addPage(); y = 15; }
        const isBreach = entry.compliance === 'EXCURSION';
        if (isBreach) { pdf.setTextColor(220, 38, 38); } else { pdf.setTextColor(40, 40, 40); }

        pdf.text(String(entry.entry_number), cols[0], y);
        pdf.text(entry.entry_type, cols[1], y);
        pdf.text((entry.custodian_name || entry.location_name || '—').slice(0, 20), cols[2], y);
        pdf.text(entry.temp_range || '—', cols[3], y);
        pdf.text(entry.compliance, cols[4], y);

        let duration = '—';
        if (entry.started_at && entry.ended_at) {
          const ms = new Date(entry.ended_at) - new Date(entry.started_at);
          const hrs = Math.round(ms / 3600000 * 10) / 10;
          duration = `${hrs}h`;
        }
        pdf.text(duration, cols[5], y);
        pdf.text(entry.entry_hash ? entry.entry_hash.slice(0, 16) + '...' : '—', cols[6], y);
        y += 6;
      });

      y += 10;
      if (y > 260) { pdf.addPage(); y = 15; }

      // Master Hash
      pdf.setFillColor(13, 148, 136);
      pdf.rect(15, y, w - 30, 0.5, 'F');
      y += 5;
      pdf.setTextColor(13, 148, 136);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Master SHA-256 Chain Hash:', 15, y);
      y += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.text(data.master_hash || 'Not yet sealed', 15, y);
      y += 8;

      // Footer
      pdf.setTextColor(150, 150, 150);
      pdf.setFontSize(6);
      pdf.text(`Generated: ${new Date().toISOString()} | Track0tic track verified`, 15, y);
      if (data.client_display_name) {
        pdf.text(`Co-branded for: ${data.client_display_name}`, 15, y + 4);
      }
      pdf.text('Any tampering of entries breaks the SHA-256 hash chain — mathematically provable.', 15, y + 8);

      pdf.save(`compliance-${data.passport_uuid.slice(0, 8)}.pdf`);
    } catch (err) {
      // Silently handled
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-teal-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-sm w-full text-center">
          <AlertTriangle className="w-14 h-14 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">QR Not Found</h2>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  const status = data.status;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4" data-testid="passport-public-page">
      <div className="max-w-lg mx-auto space-y-4 py-4">

        {/* Header with co-branding */}
        <div className="text-center space-y-1">
          <div className="flex items-center justify-center gap-1">
            <span className="text-lg font-bold text-white">Track<span className="text-red-500">0</span>tic<sup className="text-[0.5em] align-super opacity-60">™</sup></span>
          </div>
          {data.client_display_name && (
            <div className="flex items-center justify-center gap-2">
              {data.client_logo_url && <img src={data.client_logo_url} alt="" className="h-6 rounded" />}
              <span className="text-sm text-slate-400">{data.client_display_name}</span>
            </div>
          )}
          <p className="text-xs text-slate-500">{data.tagline || 'track verified'}</p>
        </div>

        {/* Status Banner */}
        {status === 'dormant' && (
          <div className="bg-slate-700/50 border border-slate-600 rounded-xl p-5 text-center" data-testid="status-dormant">
            <Package className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-300 font-semibold">Journey Not Yet Started</p>
            <p className="text-slate-500 text-sm mt-1">Cold chain journey not yet started. Contact your distributor before use.</p>
            {data.distributor_name && (
              <p className="text-teal-400 text-sm mt-3">{data.distributor_name}{data.distributor_contact ? ` · ${data.distributor_contact}` : ''}</p>
            )}
          </div>
        )}

        {status === 'pre_network' && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 text-center" data-testid="status-pre-network">
            <div className="relative w-14 h-14 mx-auto mb-3">
              <Wifi className="w-14 h-14 text-amber-500/30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="w-3 h-3 bg-amber-500 rounded-full animate-ping" />
              </div>
            </div>
            <p className="text-amber-400 font-bold">Device Paired</p>
            <p className="text-amber-300 text-sm mt-1">Awaiting first telemetry reading</p>
            <p className="text-slate-400 text-xs mt-3">
              Coolant settling · Bag environment stabilizing · Device {data.live_entry?.device_uid} recording
            </p>
            {data.live_entry?.started_at && (
              <div className="mt-3 bg-slate-800/50 rounded-lg px-4 py-2 inline-block">
                <p className="text-xs text-slate-500">Journey started at</p>
                <p className="text-amber-400 font-mono text-sm">{formatDate(data.live_entry.started_at)}</p>
              </div>
            )}
            <p className="text-slate-600 text-[10px] mt-3">
              Temperature data will appear once the device completes its first cloud sync. This typically takes 1–3 minutes.
            </p>
          </div>
        )}

        {status === 'active' && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center" data-testid="status-active">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 bg-blue-500 rounded-full animate-pulse" />
              <span className="text-blue-400 font-bold">LIVE — In Transit</span>
            </div>
            <p className="text-slate-400 text-sm">Entry {data.live_entry?.entry_number} open · Device {data.live_entry?.device_uid}</p>

            {/* Receiver confirm-receipt action */}
            <div className="mt-4 pt-4 border-t border-blue-500/20">
              <p className="text-slate-300 text-sm mb-2">Have you received this shipment?</p>
              <Button
                onClick={async () => {
                  if (!window.confirm('Confirm that you have received the shipment? This will close the cold chain record.')) return;
                  try {
                    await axios.post(`${API_BASE}/api/v1/public/passport/${passportUuid}/confirm-receipt`);
                    await fetchData();
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Failed to confirm receipt');
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-receipt-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Confirm Receipt &amp; Close
              </Button>
            </div>
          </div>
        )}

        {status === 'breach' && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center" data-testid="status-breach">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
            <p className="text-red-400 font-bold text-lg">TEMPERATURE BREACH</p>
            <p className="text-slate-400 text-sm">Excursion detected. Cold chain compromised. See details below.</p>
          </div>
        )}

        {status === 'closed' && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center" data-testid="status-closed">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
            <p className="text-green-400 font-bold text-lg">{data.has_breach ? 'JOURNEY COMPLETE — EXCURSION RECORDED' : 'VERIFIED'}</p>
            <p className="text-slate-400 text-sm">All entries locked and sealed</p>
          </div>
        )}

        {/* Custodian Chain */}
        {data.custodian_chain?.length > 0 && status !== 'dormant' && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Chain of Custody</p>
            <div className="flex items-center gap-2 flex-wrap">
              {data.custodian_chain.map((name, idx) => (
                <div key={`c-${idx}`} className="flex items-center gap-2">
                  {idx > 0 && <span className="text-teal-500">→</span>}
                  <span className="text-white text-sm font-medium bg-slate-700 px-2 py-1 rounded">{name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Entries */}
        {data.entries?.length > 0 && status !== 'dormant' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Journey Entries ({data.total_entries})</p>
            {data.entries.map((entry) => {
              const isBreach = entry.compliance === 'EXCURSION';
              const isLive = entry.is_live;
              const isPreNetwork = isLive && entry.readings_count === 0;
              return (
                <div key={`e-${entry.entry_number}`} className={cn(
                  "bg-slate-800 border rounded-xl p-4",
                  isBreach ? "border-red-500/50" : isPreNetwork ? "border-amber-500/30" : isLive ? "border-blue-500/50" : "border-slate-700"
                )}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold",
                        isBreach ? "bg-red-500 text-white" : isPreNetwork ? "bg-amber-500 text-white" : isLive ? "bg-blue-500 text-white" : "bg-teal-500 text-white"
                      )}>
                        {entry.entry_number}
                      </div>
                      <Badge className={cn(
                        "text-xs",
                        entry.entry_type === 'transit' ? "bg-blue-900/30 text-blue-400" : "bg-purple-900/30 text-purple-400"
                      )}>
                        {entry.entry_type === 'transit' ? 'Transit' : 'HUB'}
                      </Badge>
                      {isPreNetwork && <Badge className="bg-amber-500/20 text-amber-400 text-xs animate-pulse">SYNCING</Badge>}
                      {isLive && !isPreNetwork && <Badge className="bg-blue-500 text-white text-xs animate-pulse">LIVE</Badge>}
                    </div>
                    {isPreNetwork ? (
                      <Badge className="text-xs font-bold bg-amber-900/30 text-amber-400">AWAITING DATA</Badge>
                    ) : (
                      <Badge className={cn(
                        "text-xs font-bold",
                        isBreach ? "bg-red-900/30 text-red-400" : "bg-green-900/30 text-green-400"
                      )}>
                        {entry.compliance}
                      </Badge>
                    )}
                  </div>

                  {isPreNetwork && (
                    <div className="bg-amber-500/5 rounded-lg p-3 mb-2 text-center">
                      <p className="text-amber-400 text-xs">Coolant settling · Bag stabilizing · First sync pending</p>
                    </div>
                  )}

                  {entry.custodian_name && (
                    <p className="text-white text-sm mb-1">{entry.custodian_name}</p>
                  )}

                  <div className="grid grid-cols-3 gap-2 text-xs text-slate-400 mt-2">
                    {entry.temp_range && (
                      <div className="flex items-center gap-1">
                        <Thermometer className={cn("w-3 h-3", isBreach ? "text-red-400" : "text-teal-400")} />
                        <span>{entry.temp_range}</span>
                      </div>
                    )}
                    {entry.device_uid && (
                      <div className="flex items-center gap-1">
                        <Truck className="w-3 h-3 text-slate-500" />
                        <span className="font-mono">{entry.device_uid}</span>
                      </div>
                    )}
                    {entry.breach_count > 0 && (
                      <div className="flex items-center gap-1 text-red-400">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{entry.breach_count} breach(es)</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                    <span>{formatDate(entry.started_at)}{entry.ended_at ? ` → ${formatDate(entry.ended_at)}` : ''}</span>
                    {entry.locked && (
                      <div className="flex items-center gap-1 text-teal-500">
                        <Lock className="w-3 h-3" />
                        <span className="font-mono">{entry.entry_hash?.slice(0, 12)}...</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Master Hash */}
        {data.master_hash && status === 'closed' && (
          <div className="bg-slate-800 border border-teal-700/50 rounded-xl p-4">
            <p className="text-xs text-teal-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Hash className="w-3 h-3" />Master SHA-256 Chain Hash
            </p>
            <p className="font-mono text-xs text-teal-400 break-all">{data.master_hash}</p>
            <p className="text-[10px] text-slate-600 mt-2">Any tampering of any entry breaks this hash. Mathematically provable.</p>
          </div>
        )}

        {/* Certificate Download */}
        {data.certificate_available && (
          <Button
            onClick={generateCertificate}
            disabled={generating}
            className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="download-cert-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating PDF...</>
            ) : (
              <><Download className="w-5 h-5 mr-2" />Download Compliance Certificate</>
            )}
          </Button>
        )}

        {/* Product Info */}
        {(data.invoice_number || data.product_description) && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-sm">
            {data.invoice_number && <div className="flex justify-between"><span className="text-slate-500">Invoice</span><span className="text-white">{data.invoice_number}</span></div>}
            {data.product_description && <div className="flex justify-between mt-1"><span className="text-slate-500">Product</span><span className="text-white">{data.product_description}</span></div>}
            {data.customer_name && <div className="flex justify-between mt-1"><span className="text-slate-500">Customer</span><span className="text-white">{data.customer_name}</span></div>}
          </div>
        )}

        <p className="text-center text-slate-600 text-[10px] pt-2">
          Powered by Track<span className="text-red-500">0</span>tic<sup className="text-[0.5em] align-super opacity-60">™</sup> | {data?.tagline || 'track verified'}
        </p>
      </div>
    </div>
  );
}
